import React, { useState, useMemo } from 'react';
import { format, addDays, isBefore, isAfter, isSameDay, differenceInDays, startOfDay } from 'date-fns';
import { Calendar } from '../components/ui/calendar';
import { Button } from '../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { CalendarIcon, Percent, Clock, AlertCircle, Truck, Info } from 'lucide-react';

const MIN_RENTAL_DAYS = 3;
const DAILY_RATE = 99.99;
const EXPEDITED_SHIPPING_FEE = 50;
const EXPEDITED_CUTOFF_DAYS = 6; // 3-6 days = expedited
const STANDARD_CUTOFF_DAYS = 7;  // 7+ days = free shipping
const BLOCKED_DAYS = 2;          // Block dates within 48 hours

const PRESET_DURATIONS = [
  { label: '3 Days', days: 3, discount: 15 },
  { label: '7 Days', days: 7, discount: 25 },
  { label: '14 Days', days: 14, discount: 25 },
];

const RentalCalendar = ({ 
  bookedDates = [], 
  onDateChange, 
  selectedRange,
  dailyRate = DAILY_RATE,
  bufferDays = 2,
  onShippingChange
}) => {
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [isStartOpen, setIsStartOpen] = useState(false);
  const [isEndOpen, setIsEndOpen] = useState(false);

  const today = startOfDay(new Date());
  
  // Blocked dates: within 48 hours (2 days)
  const blockedUntil = addDays(today, BLOCKED_DAYS);
  
  // Expedited shipping: 3-6 days from today
  const expeditedStart = addDays(today, BLOCKED_DAYS + 1); // Day 3
  const expeditedEnd = addDays(today, EXPEDITED_CUTOFF_DAYS); // Day 6
  
  // Standard (free) shipping: 7+ days from today
  const standardStart = addDays(today, STANDARD_CUTOFF_DAYS); // Day 7+

  // Convert booked dates strings to Date objects
  const bookedDateObjects = useMemo(() => 
    bookedDates.map(d => new Date(d)), [bookedDates]
  );

  const isDateBooked = (date) => {
    return bookedDateObjects.some(booked => isSameDay(booked, date));
  };

  // Determine shipping tier for a date
  const getShippingTier = (date) => {
    const d = startOfDay(date);
    const daysFromToday = differenceInDays(d, today);
    
    if (daysFromToday <= BLOCKED_DAYS) {
      return 'blocked';
    } else if (daysFromToday <= EXPEDITED_CUTOFF_DAYS) {
      return 'expedited'; // 3-6 days
    } else {
      return 'standard'; // 7+ days
    }
  };

  const isDateDisabled = (date) => {
    const d = startOfDay(date);
    // Block dates within 48 hours
    if (isBefore(d, blockedUntil) || isSameDay(d, blockedUntil)) return true;
    if (isDateBooked(d)) return true;
    // Check buffer days before booked dates
    for (const booked of bookedDateObjects) {
      const bufferStart = addDays(booked, -bufferDays);
      if (isAfter(d, bufferStart) && isBefore(d, booked)) {
        return true;
      }
    }
    return false;
  };

  // Custom day renderer for shipping tier colors
  const getDayClassName = (date) => {
    const d = startOfDay(date);
    if (isDateDisabled(d)) return '';
    
    const tier = getShippingTier(d);
    if (tier === 'expedited') {
      return 'bg-[#90EE90]/30 hover:bg-[#90EE90]/50 text-gray-900'; // Light green
    } else if (tier === 'standard') {
      return 'bg-[#228B22]/30 hover:bg-[#228B22]/50 text-gray-900'; // Dark green
    }
    return '';
  };

  const handleStartDateSelect = (date) => {
    if (date) {
      const tier = getShippingTier(date);
      const minEndDate = addDays(date, MIN_RENTAL_DAYS - 1);
      onDateChange({ start: date, end: minEndDate });
      setIsStartOpen(false);
      setSelectedPreset(MIN_RENTAL_DAYS);
      
      // Notify parent of shipping tier
      if (onShippingChange) {
        onShippingChange(tier === 'expedited' ? EXPEDITED_SHIPPING_FEE : 0);
      }
    }
  };

  const handleEndDateSelect = (date) => {
    if (date && selectedRange?.start) {
      const days = differenceInDays(date, selectedRange.start) + 1;
      if (days < MIN_RENTAL_DAYS) {
        const minEndDate = addDays(selectedRange.start, MIN_RENTAL_DAYS - 1);
        onDateChange({ start: selectedRange.start, end: minEndDate });
      } else {
        onDateChange({ start: selectedRange.start, end: date });
      }
      setIsEndOpen(false);
      setSelectedPreset(null);
    }
  };

  const handlePresetClick = (preset) => {
    if (selectedRange?.start) {
      const endDate = addDays(selectedRange.start, preset.days - 1);
      let isValid = true;
      let current = selectedRange.start;
      while (current <= endDate) {
        if (isDateBooked(current)) {
          isValid = false;
          break;
        }
        current = addDays(current, 1);
      }
      if (isValid) {
        onDateChange({ start: selectedRange.start, end: endDate });
        setSelectedPreset(preset.days);
      }
    }
  };

  // Calculate shipping tier for selected start date
  const selectedShippingTier = useMemo(() => {
    if (!selectedRange?.start) return null;
    return getShippingTier(selectedRange.start);
  }, [selectedRange?.start]);

  const shippingFee = selectedShippingTier === 'expedited' ? EXPEDITED_SHIPPING_FEE : 0;

  // Calculate rental details with discounts
  const rentalDetails = useMemo(() => {
    if (!selectedRange?.start || !selectedRange?.end) {
      return { days: 0, discount: 0, subtotal: 0, discountAmount: 0, total: 0, isValid: false };
    }
    
    const days = differenceInDays(selectedRange.end, selectedRange.start) + 1;
    const isValid = days >= MIN_RENTAL_DAYS;
    
    let discountPercent = 0;
    if (days >= 7) {
      discountPercent = 25;
    } else if (days >= 3) {
      discountPercent = 15;
    }
    
    const subtotal = days * dailyRate;
    const discountAmount = (subtotal * discountPercent) / 100;
    const rentalTotal = subtotal - discountAmount;
    const total = rentalTotal + shippingFee;
    
    return {
      days,
      discount: discountPercent,
      subtotal,
      discountAmount,
      rentalTotal,
      shippingFee,
      total,
      isValid
    };
  }, [selectedRange, dailyRate, shippingFee]);

  const returnDate = selectedRange?.end ? addDays(selectedRange.end, bufferDays) : null;

  return (
    <div className="space-y-6" data-testid="rental-calendar">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
        <CalendarIcon className="w-6 h-6 text-[#C6A87C]" />
        <div>
          <h3 className="font-['Barlow_Condensed'] text-xl font-semibold uppercase tracking-tight">
            Select Rental Dates
          </h3>
          <p className="text-sm text-gray-500">
            Minimum {MIN_RENTAL_DAYS}-day rental at ${dailyRate}/day
          </p>
        </div>
      </div>

      {/* Date Pickers */}
      <div className="grid grid-cols-2 gap-4">
        {/* Start Date */}
        <div>
          <p className="text-xs text-[#C6A87C] uppercase tracking-widest mb-2 font-semibold">Start Date</p>
          <Popover open={isStartOpen} onOpenChange={setIsStartOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal h-14 bg-gray-50 border-gray-200 hover:border-[#C6A87C] hover:bg-gray-100"
                data-testid="start-date-picker"
              >
                <CalendarIcon className="mr-3 h-5 w-5 text-[#C6A87C]" />
                {selectedRange?.start ? (
                  <span className="font-semibold">{format(selectedRange.start, 'MMM d, yyyy')}</span>
                ) : (
                  <span className="text-gray-500">Pick start date</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-white border-gray-200" align="start">
              <Calendar
                mode="single"
                selected={selectedRange?.start}
                onSelect={handleStartDateSelect}
                disabled={isDateDisabled}
                modifiers={{
                  expedited: (date) => getShippingTier(date) === 'expedited' && !isDateDisabled(date),
                  standard: (date) => getShippingTier(date) === 'standard' && !isDateDisabled(date),
                }}
                modifiersClassNames={{
                  expedited: 'bg-[#90EE90]/30 hover:bg-[#90EE90]/50',
                  standard: 'bg-[#228B22]/30 hover:bg-[#228B22]/50',
                }}
                initialFocus
                className="bg-white"
              />
              {/* Calendar Legend */}
              <div className="p-3 border-t border-gray-200 bg-gray-100">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-4 h-4 bg-[#228B22]/50 rounded-sm"></span>
                    <span>Free Shipping</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-4 h-4 bg-[#90EE90]/50 rounded-sm"></span>
                    <span>Expedited (+$50)</span>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* End Date */}
        <div>
          <p className="text-xs text-[#C6A87C] uppercase tracking-widest mb-2 font-semibold">End Date</p>
          <Popover open={isEndOpen} onOpenChange={setIsEndOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal h-14 bg-gray-50 border-gray-200 hover:border-[#C6A87C] hover:bg-gray-100"
                disabled={!selectedRange?.start}
                data-testid="end-date-picker"
              >
                <CalendarIcon className="mr-3 h-5 w-5 text-[#C6A87C]" />
                {selectedRange?.end ? (
                  <span className="font-semibold">{format(selectedRange.end, 'MMM d, yyyy')}</span>
                ) : (
                  <span className="text-gray-500">Pick end date</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-white border-gray-200" align="start">
              <Calendar
                mode="single"
                selected={selectedRange?.end}
                onSelect={handleEndDateSelect}
                disabled={(date) => {
                  if (isDateDisabled(date)) return true;
                  if (!selectedRange?.start) return true;
                  const minEnd = addDays(selectedRange.start, MIN_RENTAL_DAYS - 1);
                  if (isBefore(date, minEnd)) return true;
                  return false;
                }}
                initialFocus
                className="bg-white"
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Shipping Tier Indicator */}
      {selectedRange?.start && (
        <div className={`p-4 border ${
          selectedShippingTier === 'expedited' 
            ? 'bg-[#90EE90]/10 border-[#90EE90]/30' 
            : 'bg-[#228B22]/10 border-[#228B22]/30'
        }`} data-testid="shipping-tier">
          <div className="flex items-center gap-3">
            <Truck className={`w-5 h-5 ${selectedShippingTier === 'expedited' ? 'text-[#90EE90]' : 'text-[#228B22]'}`} />
            <div>
              <p className="font-semibold">
                {selectedShippingTier === 'expedited' ? (
                  <>Expedited Shipping <span className="text-[#C6A87C]">+$50</span></>
                ) : (
                  <>Standard Shipping <span className="text-[#228B22]">FREE</span></>
                )}
              </p>
              <p className="text-xs text-gray-500">
                {selectedShippingTier === 'expedited' 
                  ? 'Your start date is within 3-6 days. Expedited shipping fee applies.'
                  : 'Your start date is 7+ days out. Free standard shipping!'
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Quick Select Presets */}
      <div>
        <p className="text-xs text-gray-500 uppercase tracking-widest mb-3">Quick Select Duration</p>
        <div className="grid grid-cols-3 gap-3">
          {PRESET_DURATIONS.map((preset) => (
            <Button
              key={preset.days}
              variant={selectedPreset === preset.days ? "default" : "outline"}
              onClick={() => handlePresetClick(preset)}
              disabled={!selectedRange?.start}
              className={`relative h-auto py-3 px-4 flex flex-col items-center gap-1 ${
                selectedPreset === preset.days 
                  ? 'bg-[#C6A87C] text-black hover:bg-[#D4B88F] border-[#C6A87C]' 
                  : 'border-gray-200 hover:border-[#C6A87C] hover:text-[#C6A87C] bg-gray-100'
              }`}
              data-testid={`preset-${preset.days}`}
            >
              <span className="font-['Barlow_Condensed'] text-lg font-semibold uppercase tracking-wider">
                {preset.label}
              </span>
              <span className={`text-xs flex items-center gap-1 ${
                selectedPreset === preset.days ? 'text-black/70' : 'text-[#C6A87C]'
              }`}>
                <Percent className="w-3 h-3" />
                {preset.discount}% OFF
              </span>
            </Button>
          ))}
        </div>
      </div>

      {/* Pricing Summary */}
      {rentalDetails.days >= MIN_RENTAL_DAYS && (
        <div className="bg-gray-50 border border-gray-200 p-5 space-y-4" data-testid="rental-summary">
          {/* Duration */}
          <div className="flex justify-between items-center">
            <span className="text-gray-500 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Rental Duration
            </span>
            <span className="font-['Barlow_Condensed'] text-xl font-semibold">
              {rentalDetails.days} {rentalDetails.days === 1 ? 'Day' : 'Days'}
            </span>
          </div>

          {/* Subtotal */}
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-500">
              ${dailyRate}/day × {rentalDetails.days} days
            </span>
            <span>${rentalDetails.subtotal.toFixed(2)}</span>
          </div>

          {/* Discount */}
          {rentalDetails.discount > 0 && (
            <div className="flex justify-between items-center text-sm bg-[#C6A87C]/10 -mx-5 px-5 py-3 border-y border-[#C6A87C]/20">
              <span className="text-[#C6A87C] flex items-center gap-2 font-semibold">
                <Percent className="w-4 h-4" />
                Duration Discount ({rentalDetails.discount}%)
              </span>
              <span className="text-[#C6A87C] font-semibold">
                -${rentalDetails.discountAmount.toFixed(2)}
              </span>
            </div>
          )}

          {/* Rental Total */}
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-500">Rental Subtotal</span>
            <span>${rentalDetails.rentalTotal.toFixed(2)}</span>
          </div>

          {/* Shipping */}
          <div className={`flex justify-between items-center text-sm ${
            shippingFee > 0 ? 'bg-[#90EE90]/10 -mx-5 px-5 py-3 border-y border-[#90EE90]/20' : ''
          }`}>
            <span className="flex items-center gap-2">
              <Truck className="w-4 h-4" />
              {shippingFee > 0 ? 'Expedited Shipping' : 'Standard Shipping'}
            </span>
            <span className={shippingFee > 0 ? 'text-[#90EE90] font-semibold' : 'text-[#228B22] font-semibold'}>
              {shippingFee > 0 ? `+$${shippingFee.toFixed(2)}` : 'FREE'}
            </span>
          </div>

          {/* Total */}
          <div className="flex justify-between items-center pt-2 border-t border-gray-200">
            <span className="font-['Barlow_Condensed'] text-lg font-semibold uppercase tracking-wider">
              Total
            </span>
            <div className="text-right">
              <span className="font-['Barlow_Condensed'] text-3xl font-bold text-[#C6A87C]">
                ${rentalDetails.total.toFixed(2)}
              </span>
              {(rentalDetails.discount > 0 || shippingFee === 0) && (
                <p className="text-xs text-gray-500">
                  {rentalDetails.discount > 0 && `You save $${rentalDetails.discountAmount.toFixed(2)}`}
                  {rentalDetails.discount > 0 && shippingFee === 0 && ' + '}
                  {shippingFee === 0 && 'Free shipping!'}
                </p>
              )}
            </div>
          </div>

          {/* Return Date */}
          {returnDate && (
            <div className="flex justify-between items-center text-sm pt-3 border-t border-gray-200">
              <span className="text-gray-500">
                Return By (includes {bufferDays}-day buffer)
              </span>
              <span className="text-[#C6A87C] font-semibold">
                {format(returnDate, 'MMM d, yyyy')}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Delivery Note */}
      {selectedRange?.start && (
        <div className="flex items-start gap-2 p-3 bg-white border border-gray-200 text-sm">
          <Info className="w-4 h-4 text-[#C6A87C] mt-0.5 flex-shrink-0" />
          <p className="text-gray-500">
            <strong className="font-semibold" style={{ color: '#333333' }}>Order will arrive at your destination 1 day before your first rental day.</strong>
            {' '}This ensures you have time to try on and adjust your gear before hitting the road.
          </p>
        </div>
      )}

      {/* Shipping Legend */}
      <div className="border border-gray-200 p-4 bg-white/50">
        <p className="text-xs text-gray-500 uppercase tracking-widest mb-3 font-semibold">Shipping & Calendar Legend</p>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-3">
            <span className="w-5 h-5 bg-[#228B22]/50 rounded-sm flex-shrink-0"></span>
            <div>
              <p className="font-semibold text-[#228B22]">Free Shipping</p>
              <p className="text-xs text-gray-500">7+ days in advance</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="w-5 h-5 bg-[#90EE90]/50 rounded-sm flex-shrink-0"></span>
            <div>
              <p className="font-semibold text-[#90EE90]">Expedited (+$50)</p>
              <p className="text-xs text-gray-500">3-6 days in advance</p>
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-3 pt-3 border-t border-gray-200">
          <AlertCircle className="w-3 h-3 inline mr-1" />
          Dates within 48 hours are blocked to allow for preparation and packing.
        </p>
      </div>

      {/* Discount Info - shown before dates selected */}
      {!rentalDetails.days && (
        <div className="bg-[#C6A87C]/5 border border-[#C6A87C]/20 p-4">
          <p className="text-sm font-semibold text-[#C6A87C] mb-2 flex items-center gap-2">
            <Percent className="w-4 h-4" />
            Duration Discounts Available
          </p>
          <ul className="text-xs text-gray-500 space-y-1">
            <li>• Rent for <strong style={{ color: '#333333' }}>3+ days</strong> → <span className="text-[#C6A87C]">15% off</span></li>
            <li>• Rent for <strong style={{ color: '#333333' }}>7+ days</strong> → <span className="text-[#C6A87C]">25% off</span></li>
          </ul>
        </div>
      )}

      {/* Availability Legend */}
      {bookedDates.length > 0 && (
        <div className="flex items-center gap-4 text-xs text-gray-500 pt-2">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-red-400 rounded-sm"></span>
            <span>Unavailable (Booked)</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default RentalCalendar;
